import { useState, useEffect, useRef } from 'react';

const plans = [
  {
    name: 'Starter',
    price: 297,
    badge: null,
    description: 'Perfect for small home service businesses ready to start automating.',
    color: 'from-cyan-400/20 to-cyan-600/10',
    borderColor: 'border-cyan-400/30',
    glowColor: 'rgba(0,212,255,0.25)',
    accentColor: 'text-cyan-400',
    buttonStyle: 'border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black',
    features: [
      { icon: 'ri-robot-2-line', text: 'AI Voice Agent (1 line)' },
      { icon: 'ri-calendar-check-line', text: 'Smart Calendar Integration' },
      { icon: 'ri-mail-send-line', text: 'Automated Follow-Up Sequences' },
      { icon: 'ri-customer-service-2-line', text: 'Lead Capture Form' },
      { icon: 'ri-message-3-line', text: 'SMS & Email Notifications' },
      { icon: 'ri-bar-chart-line', text: 'Basic Analytics Dashboard' },
    ],
    notIncluded: [
      'Website Design',
      'CRM Automation',
      'Multi-channel Lead System',
    ],
  },
  {
    name: 'Professional',
    price: 497,
    badge: 'Most Popular',
    description: 'The complete AI automation suite for growing home service companies.',
    color: 'from-blue-500/30 to-cyan-400/20',
    borderColor: 'border-cyan-400/60',
    glowColor: 'rgba(0,212,255,0.45)',
    accentColor: 'text-cyan-400',
    buttonStyle: 'bg-gradient-to-r from-cyan-400 to-blue-600 text-white hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] hover:scale-105',
    features: [
      { icon: 'ri-robot-2-line', text: 'AI Voice Agent (3 lines)' },
      { icon: 'ri-calendar-check-line', text: 'Smart Calendar + Team Sync' },
      { icon: 'ri-mail-send-line', text: 'Advanced Follow-Up Automation' },
      { icon: 'ri-layout-line', text: 'Custom Website Design' },
      { icon: 'ri-customer-service-2-line', text: 'Multi-Channel Lead Capture' },
      { icon: 'ri-database-2-line', text: 'Full CRM Automation' },
      { icon: 'ri-message-3-line', text: 'SMS, Email & Chat Automation' },
      { icon: 'ri-bar-chart-2-line', text: 'Advanced Analytics & Reporting' },
      { icon: 'ri-headphone-line', text: 'Priority Support' },
    ],
    notIncluded: [],
  },
  {
    name: 'Custom',
    price: null,
    badge: 'Enterprise',
    description: 'Tailored AI systems built around your exact business needs and scale.',
    color: 'from-purple-500/20 to-blue-600/10',
    borderColor: 'border-white/20',
    glowColor: 'rgba(139,92,246,0.2)',
    accentColor: 'text-white',
    buttonStyle: 'border-2 border-white/40 text-white hover:border-cyan-400 hover:text-cyan-400',
    features: [
      { icon: 'ri-settings-4-line', text: 'Everything in Professional' },
      { icon: 'ri-robot-2-line', text: 'Unlimited AI Voice Lines' },
      { icon: 'ri-building-line', text: 'Multi-Location Support' },
      { icon: 'ri-code-s-slash-line', text: 'Custom Integrations & API Access' },
      { icon: 'ri-team-line', text: 'Dedicated Account Manager' },
      { icon: 'ri-shield-star-line', text: 'White-Glove Onboarding' },
      { icon: 'ri-24-hours-line', text: '24/7 Priority Support' },
    ],
    notIncluded: [],
  },
];

const customServices = [
  { id: 'voice1', icon: 'ri-robot-2-line', name: 'AI Voice Agent', desc: '1 dedicated line', price: 97 },
  { id: 'voice3', icon: 'ri-robot-2-line', name: 'AI Voice Agent (3 Lines)', desc: 'Multi-line coverage', price: 247 },
  { id: 'calendar', icon: 'ri-calendar-check-line', name: 'Smart Calendar Integration', desc: 'Auto-booking & reminders', price: 49 },
  { id: 'followup', icon: 'ri-mail-send-line', name: 'Automated Follow-Up', desc: 'SMS & email sequences', price: 59 },
  { id: 'website', icon: 'ri-layout-line', name: 'Custom Website Design', desc: 'Conversion-optimized site', price: 149 },
  { id: 'crm', icon: 'ri-database-2-line', name: 'Full CRM Automation', desc: 'Pipeline & contact mgmt', price: 79 },
  { id: 'leadcapture', icon: 'ri-customer-service-2-line', name: 'Multi-Channel Lead Capture', desc: 'Web, social & chat', price: 69 },
  { id: 'analytics', icon: 'ri-bar-chart-2-line', name: 'Advanced Analytics', desc: 'Reports & insights', price: 39 },
  { id: 'multiloc', icon: 'ri-building-line', name: 'Multi-Location Support', desc: 'Manage multiple branches', price: 99 },
  { id: 'api', icon: 'ri-code-s-slash-line', name: 'Custom API Integrations', desc: 'Connect your existing tools', price: 129 },
];

export default function Pricing() {
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleService = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const customTotal = customServices
    .filter(s => selected.has(s.id))
    .reduce((sum, s) => sum + s.price, 0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-black via-[#050810] to-black overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-cyan-500/8 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-blue-600/8 rounded-full blur-3xl" />

      {/* Grid Lines */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(0,212,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.3) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 backdrop-blur-sm mb-6">
            <i className="ri-price-tag-3-line text-cyan-400 text-sm" />
            <span className="text-xs font-bold text-cyan-400 tracking-wider uppercase">Simple Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Invest in Your Business.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Watch It Grow.
            </span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            No hidden fees. No long-term contracts. Just powerful AI automation that pays for itself.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-3xl border backdrop-blur-xl transition-all duration-700 overflow-hidden
                ${plan.borderColor}
                ${plan.name === 'Professional' ? 'shadow-[0_0_80px_rgba(0,212,255,0.3)] scale-105' : 'hover:shadow-[0_0_60px_rgba(0,212,255,0.2)] hover:-translate-y-2'}
                ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
              `}
              style={{
                background: `linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))`,
                transitionDelay: `${index * 150}ms`,
              }}
            >
              {/* Top Glow Bar */}
              {plan.name === 'Professional' && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
              )}

              {/* Badge */}
              {plan.badge && (
                <div className="absolute top-5 right-5">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase
                    ${plan.name === 'Professional'
                      ? 'bg-gradient-to-r from-cyan-400 to-blue-600 text-white shadow-[0_0_20px_rgba(0,212,255,0.5)]'
                      : 'bg-white/10 border border-white/20 text-white/70'
                    }`}>
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-8 flex flex-col flex-1">
                {/* Plan Name */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  {plan.price ? (
                    <div className="flex items-end gap-2">
                      <span className="text-5xl font-black text-white">${plan.price}</span>
                      <span className="text-gray-400 mb-2 text-lg">/month</span>
                    </div>
                  ) : (
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-black text-white">Let's Talk</span>
                    </div>
                  )}
                  {plan.price && (
                    <p className="text-xs text-gray-500 mt-1">Billed monthly · Cancel anytime</p>
                  )}
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-cyan-400/10 border border-cyan-400/20 shrink-0">
                        <i className={`${feature.icon} text-cyan-400 text-sm`} />
                      </div>
                      <span className="text-gray-300 text-sm">{feature.text}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 opacity-40">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 border border-white/10 shrink-0">
                        <i className="ri-close-line text-gray-500 text-sm" />
                      </div>
                      <span className="text-gray-500 text-sm line-through">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={scrollToBooking}
                  className={`w-full py-4 rounded-2xl font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${plan.buttonStyle}`}
                >
                  Get Started
                  <i className="ri-arrow-right-line text-lg" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Package Builder */}
        <div className={`mt-16 max-w-6xl mx-auto transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="relative rounded-3xl border border-white/10 backdrop-blur-xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))' }}>

            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            <div className="p-10">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 mb-3">
                    <i className="ri-tools-line text-white/70 text-xs" />
                    <span className="text-xs font-bold text-white/70 tracking-wider uppercase">Enterprise</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2">Build Your Custom Package</h3>
                  <p className="text-gray-400 text-sm max-w-lg">
                    Pick exactly what your business needs. Mix and match services — we'll build a tailored AI system around your goals.
                  </p>
                </div>
                {/* Live Total */}
                <div className="shrink-0 text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Estimated Monthly</p>
                  <div className="flex items-end gap-1 justify-end">
                    <span className="text-5xl font-black text-white">${customTotal}</span>
                    <span className="text-gray-400 mb-1 text-lg">/mo</span>
                  </div>
                  {selected.size > 0 && (
                    <p className="text-xs text-cyan-400 mt-1">{selected.size} service{selected.size > 1 ? 's' : ''} selected</p>
                  )}
                  {selected.size === 0 && (
                    <p className="text-xs text-gray-600 mt-1">Select services below</p>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" />

              {/* Service Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-10">
                {customServices.map(service => {
                  const isActive = selected.has(service.id);
                  return (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`relative flex flex-col items-start gap-2 p-4 rounded-2xl border text-left transition-all duration-200 cursor-pointer group
                        ${isActive
                          ? 'border-cyan-400/60 bg-cyan-400/10 shadow-[0_0_20px_rgba(0,212,255,0.15)]'
                          : 'border-white/10 bg-white/3 hover:border-white/25 hover:bg-white/5'
                        }`}
                    >
                      {/* Check indicator */}
                      <div className={`absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full border transition-all duration-200
                        ${isActive ? 'bg-cyan-400 border-cyan-400' : 'border-white/20 bg-transparent'}`}>
                        {isActive && <i className="ri-check-line text-black text-xs font-bold" />}
                      </div>

                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200
                        ${isActive ? 'bg-cyan-400/20 border border-cyan-400/40' : 'bg-white/5 border border-white/10'}`}>
                        <i className={`${service.icon} text-base ${isActive ? 'text-cyan-400' : 'text-gray-400'}`} />
                      </div>

                      <div>
                        <p className={`text-sm font-semibold leading-tight mb-0.5 ${isActive ? 'text-white' : 'text-gray-300'}`}>
                          {service.name}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight">{service.desc}</p>
                      </div>

                      <div className={`text-sm font-bold mt-auto ${isActive ? 'text-cyan-400' : 'text-gray-500'}`}>
                        +${service.price}/mo
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* CTA Row */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <button
                  onClick={scrollToBooking}
                  className="flex-1 sm:flex-none px-10 py-4 rounded-2xl font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border-2 border-white/40 text-white hover:border-cyan-400 hover:text-cyan-400"
                >
                  <i className="ri-calendar-line text-lg" />
                  Book a Strategy Call
                </button>
                <p className="text-xs text-gray-500 text-center sm:text-left">
                  <i className="ri-information-line mr-1" />
                  Final pricing confirmed on your onboarding call · No commitment required
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className={`text-center mt-12 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <i className="ri-shield-check-line text-green-400 text-base" />
            All plans include a one-time $59.99 onboarding fee · No hidden fees · Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}
